import React, { useEffect, useRef, useState } from 'react';
import { ChevronDown, ChevronUp } from 'lucide-react';
import { useDataClock } from '../../data';
import { useFunnelCounters } from '../../hooks/useFunnelCounters';
import ErrorState from '../../ui/ErrorState';
import { useQueryParam } from '../../app/useQueryParam';
import type { BandTab } from '../../hooks/useBandRoute';
import KeyOperatingMetrics from './KeyOperatingMetrics';
import PipelineTab from './PipelineTab';
import AlertFlowSankey from './AlertFlowSankey';
import ConnectedSourcesTab from './ConnectedSourcesTab';
import { komContext, type KomState } from '../../lib/kom';
import { DEFAULT_PERIOD, PERIOD_WINDOWS, windowSpan, type PeriodWindow } from '../../lib/funnel';
import type { FeedTarget } from '../../lib/stageDetail';

interface MetricsBandProps {
  tab: BandTab;
  onTab: (tab: BandTab) => void;
  showTab: (tab: BandTab) => void;
  onOpenCampaigns?: () => void;
  onOpenConnectors?: () => void;
  onAddSource?: () => void;
  onOpenFeed?: (view?: 'active' | 'suppressed') => void;
  /** Opens the feed with a list, a state and a type chosen (a pipeline stage's own destinations). */
  onShowInFeed?: (target: FeedTarget) => void;
}

const TABS: { id: BandTab; label: string }[] = [
  { id: 'kom', label: 'Key Operating Metrics' },
  { id: 'pipe', label: 'AI Analysis Pipeline' },
  { id: 'flow', label: 'Alert Flow' },
  { id: 'src', label: 'Connected Sources' },
];

const HIDDEN_KEY = 'arbitium-c3-overview-hidden';

/** The Overview can be folded away to leave the room to the workspace and the feed; the choice is kept in the browser. */
function readHidden(): boolean {
  try {
    return localStorage.getItem(HIDDEN_KEY) === '1';
  } catch {
    return false;
  }
}

const MetricsBand: React.FC<MetricsBandProps> = ({
  tab,
  onTab,
  showTab,
  onOpenCampaigns,
  onOpenConnectors,
  onAddSource,
  onOpenFeed,
  onShowInFeed,
}) => {
  // Every figure of the band is measured over one period: the last 24 hours until the picker says otherwise (client
  // notes 10/26, item 8). It is kept in the address, so a reload and a shared link read the same period.
  const [period, setPeriod] = useQueryParam<PeriodWindow>('period', DEFAULT_PERIOD, PERIOD_WINDOWS);
  // Demo or Live, the counters come from the data source: Demo counts its invented tenant, so the band
  // agrees with the feed and the graph (item 1).
  const { counters, loading, loaded, error } = useFunnelCounters(true, period);
  const { refreshNow } = useDataClock();
  // Until the first read succeeds the counters are empty placeholders, not real zeros.
  const state: KomState = loaded ? 'ready' : loading ? 'loading' : error ? 'error' : 'ready';
  const [hidden, setHidden] = useState(readHidden);
  const toggleHidden = () => {
    const next = !hidden;
    setHidden(next);
    try {
      localStorage.setItem(HIDDEN_KEY, next ? '1' : '0');
    } catch {
      /* ignore quota / private mode */
    }
  };
  // Another view that asks for a tab of the band (a stage, a tile) must be able to show it.
  const lastTab = useRef(tab);
  useEffect(() => {
    if (lastTab.current !== tab) setHidden(false);
    lastTab.current = tab;
  }, [tab]);

  return (
    <section
      id="c3-overview"
      className={`c3-band c3-metrics-band${loading ? ' is-loading' : ''}${hidden ? ' is-hidden' : ''}`}
      aria-busy={loading}
    >
      <div className="c3-overview-head">
        <div>
          <h2 className="c3-overview-title">Overview</h2>
          <div className="c3-overview-context">
            <select
              className="c3-select c3-overview-period"
              aria-label="Period"
              value={period}
              onChange={(e) => setPeriod(e.target.value as PeriodWindow)}
            >
              {PERIOD_WINDOWS.map((item) => (
                <option key={item} value={item}>
                  Last {windowSpan(item)}
                </option>
              ))}
            </select>
            {komContext(counters) ? <span>· {komContext(counters)}</span> : null}
            <span className="c3-metrics-loading" aria-live="polite">
              {loading ? '· Updating…' : ''}
            </span>
          </div>
        </div>
        <div className="c3-metrics-tabs">
          {!hidden && (
            <div className="c3-metrics-tabs-list" role="tablist" aria-label="Overview views">
              {TABS.map((item) => (
                <button
                  key={item.id}
                  type="button"
                  role="tab"
                  aria-selected={tab === item.id}
                  className={`c3-metrics-tab${tab === item.id ? ' is-active' : ''}`}
                  onClick={() => onTab(item.id)}
                >
                  {item.label}
                </button>
              ))}
            </div>
          )}
          <button
            type="button"
            className="c3-metrics-tab c3-overview-toggle"
            aria-expanded={!hidden}
            aria-controls="c3-overview-body"
            onClick={toggleHidden}
          >
            {hidden ? (
              <ChevronDown size={16} strokeWidth={1.5} aria-hidden />
            ) : (
              <ChevronUp size={16} strokeWidth={1.5} aria-hidden />
            )}
            {hidden ? 'Show overview' : 'Hide overview'}
          </button>
        </div>
      </div>
      <div id="c3-overview-body" hidden={hidden}>
        {error && (
          <ErrorState compact title={error} onRetry={() => void refreshNow()}>
            Nothing was changed. Try again in a moment.
          </ErrorState>
        )}
        {tab === 'kom' && (
          <KeyOperatingMetrics counters={counters} state={state} onShowTab={showTab} onOpenFeed={onOpenFeed} />
        )}
        {tab === 'pipe' && (
          <PipelineTab counters={counters} state={state} onShowTab={showTab} onShowInFeed={onShowInFeed} />
        )}
        {tab === 'flow' && <AlertFlowSankey counters={counters} onOpenCampaigns={onOpenCampaigns} />}
        {tab === 'src' && (
          <ConnectedSourcesTab window={period} onOpenConnectors={onOpenConnectors} onAddSource={onAddSource} />
        )}
      </div>
    </section>
  );
};

export default MetricsBand;
